import React, { forwardRef, useImperativeHandle, useRef, useEffect } from 'react';

/**
 * AudioPlayer — plays streaming audio chunks from the WebSocket.
 *
 * Container/codec agnostic — `decodeAudioData(arrayBuffer)` handles whatever
 * the browser supports (WAV, MP3, OGG, AAC). Each TTS provider picks its own
 * format: Azure was WAV PCM until #383 (now MP3), ElevenLabs is MP3,
 * Deepgram Aura-2 is MP3.
 *
 * Uses Web Audio API (AudioContext) for all playback so the browser keeps
 * a single audio session open. This prevents the OS-level audio-route
 * switching (the "click" / quality toggle) that happens when you create a
 * new <audio> element per chunk.
 *
 * Autoplay: AudioContext is created lazily on the first user gesture
 * (warmUp() or addChunk() after a gesture).  warmUp() should be called
 * inside a click handler to unlock the context early.
 */
const AudioPlayer = forwardRef(({ onDone } = {}, ref) => {
  const ctxRef        = useRef(null);   // AudioContext — shared across all chunks
  const nextStartRef  = useRef(0);      // scheduled end-time of last queued chunk
  const isPlayingRef  = useRef(false);
  const pendingRef    = useRef(0);      // chunks scheduled but not yet ended
  const inflightRef   = useRef(0);      // decode queue depth (kept for onDone logic)
  const onDoneRef     = useRef(onDone);
  onDoneRef.current   = onDone;
  const pendingDoneRef  = useRef(false);
  // Sequential decode queue — prevents out-of-order playback when many tiny
  // chunks arrive rapidly and decodeAudioData finishes in non-arrival order.
  const decodeQueueRef  = useRef([]);   // array of {arrayBuffer, seq} waiting to decode
  const decodingRef     = useRef(false); // true while a decode is in progress
  // #605 diagnostics — track per-chunk sequence + per-stream stats so the
  // failure-mode pattern (always-first / always-last / mid-stream / specific
  // sizes / specific codec markers) is visible in the console without
  // requiring repro instrumentation. Reset by stop() between turns.
  const chunkSeqRef     = useRef(0);    // monotonic counter, resets on stop()
  const decodeStatsRef  = useRef({ ok: 0, failed: 0, firstHeadHex: null });
  // #622 — stateful PCM resampler bookkeeping. The Aura WS stream sends
  // raw Int16 PCM at the voice's native rate (16 kHz for the Dutch
  // `aura-2-beatrix-nl`, 24 kHz for some English voices). Browser
  // `AudioContext` runs at the device's preferred rate — typically
  // 48 kHz on Android tablets, 44.1 kHz on macOS. Naively creating
  // `AudioBuffer(channels, length, sourceRate)` and letting Web Audio
  // resample at play time produces an audible click at every chunk
  // boundary, because the framework's FIR resampler runs PER BUFFER
  // and pads each side with zeros where it can't see the neighbouring
  // buffer's samples. Stitching ~50 ms PCM chunks back-to-back this
  // way yielded the symptom Bas reported in Dutch on 2026-05-10:
  // "klik in midden van zinnen" (click in the middle of sentences).
  //
  // The fix: resample the Int16 input to `ctx.sampleRate` in JS with
  // linear interpolation that carries TWO bits of state across chunk
  // boundaries:
  //   - `pcmResamplePosFracRef`: the fractional source-position the
  //     next chunk should start at, in source-sample units. Without
  //     this each chunk's resampler starts at posFrac=0 and the
  //     output drifts by sub-sample amounts at every boundary.
  //   - `pcmResampleTailRef`: the very last Int16 sample of the
  //     previous chunk, so the interpolation for output samples that
  //     fall between "sample -1" (= previous chunk's tail) and
  //     "sample 0" of the new chunk has a real value to blend from
  //     instead of zero. The "blend from zero" is exactly what
  //     causes the click.
  //
  // Each chunk's `AudioBuffer` is now created at `ctx.sampleRate`
  // (matching the context exactly), so the audio framework does no
  // further resampling. Adjacent buffers concatenate sample-accurate
  // and the audible boundary disappears.
  const pcmResamplePosFracRef = useRef(0);
  const pcmResampleTailRef    = useRef(0);

  function getCtx() {
    if (!ctxRef.current) {
      ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (ctxRef.current.state === 'suspended') {
      ctxRef.current.resume().catch(() => {});
    }
    return ctxRef.current;
  }

  function scheduleBuffer(audioBuffer) {
    const ctx = getCtx();
    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(ctx.destination);

    const now = ctx.currentTime;
    const startAt = Math.max(now, nextStartRef.current);
    source.start(startAt);
    nextStartRef.current = startAt + audioBuffer.duration;

    pendingRef.current += 1;
    isPlayingRef.current = true;

    source.onended = () => {
      pendingRef.current -= 1;
      if (pendingRef.current <= 0) {
        pendingRef.current = 0;
        isPlayingRef.current = false;
        if (pendingDoneRef.current && inflightRef.current <= 0) {
          pendingDoneRef.current = false;
          if (typeof onDoneRef.current === 'function') onDoneRef.current();
        }
      }
    };
  }

  // #605 diagnostic helper — hex-dump the first N bytes so the console line
  // reveals the codec marker (MP3 FF Fx sync / OGG `OggS` / WebM 1A 45 DF A3
  // / WAV `RIFF`) without needing a packet capture.
  function _headHex(arrayBuffer, n = 16) {
    try {
      const view = new Uint8Array(arrayBuffer, 0, Math.min(n, arrayBuffer.byteLength));
      return Array.from(view).map(b => b.toString(16).padStart(2, '0')).join(' ');
    } catch {
      return '<unreadable>';
    }
  }

  // Process decode queue sequentially — one chunk at a time to preserve order.
  async function drainDecodeQueue() {
    if (decodingRef.current) return; // already draining
    decodingRef.current = true;
    while (decodeQueueRef.current.length > 0) {
      const item = decodeQueueRef.current.shift();
      const arrayBuffer = item.arrayBuffer;
      const seq = item.seq;
      inflightRef.current = decodeQueueRef.current.length;
      try {
        const ctx = getCtx();
        if (ctx.state === 'suspended') await ctx.resume();
        const copy = arrayBuffer.slice(0);
        const audioBuffer = await ctx.decodeAudioData(copy);
        scheduleBuffer(audioBuffer);
        // #605 — first successful decode per stream tells us what shape we
        // expect; later failures can be compared against this baseline.
        if (decodeStatsRef.current.ok === 0 && decodeStatsRef.current.firstHeadHex == null) {
          decodeStatsRef.current.firstHeadHex = _headHex(arrayBuffer);
          console.info(
            `[#605 AudioPlayer] first-chunk OK: seq=${seq} ` +
            `size=${arrayBuffer.byteLength} head=[${decodeStatsRef.current.firstHeadHex}]`
          );
        }
        decodeStatsRef.current.ok += 1;
      } catch (err) {
        decodeStatsRef.current.failed += 1;
        // #605 — log enough context to classify the failure mode without
        // re-running the call: chunk index, total size, first 16 bytes
        // (codec marker), and whether this is the first chunk of the stream
        // (where missing-codec-header is the usual suspect).
        const isFirstOfStream = decodeStatsRef.current.ok === 0;
        console.warn(
          `[#605 AudioPlayer] decode FAILED: seq=${seq} ` +
          `size=${arrayBuffer.byteLength} head=[${_headHex(arrayBuffer)}] ` +
          `firstOfStream=${isFirstOfStream} ` +
          `okSoFar=${decodeStatsRef.current.ok} failedSoFar=${decodeStatsRef.current.failed} ` +
          `err=${(err && err.message) || err}`,
          err
        );
      }
    }
    decodingRef.current = false;
    inflightRef.current = 0;
    // If server already signalled done and nothing left, fire onDone
    if (pendingDoneRef.current && pendingRef.current <= 0 && !isPlayingRef.current) {
      pendingDoneRef.current = false;
      if (typeof onDoneRef.current === 'function') onDoneRef.current();
    }
  }

  function decodeAndSchedule(arrayBuffer) {
    chunkSeqRef.current += 1;
    decodeQueueRef.current.push({ arrayBuffer, seq: chunkSeqRef.current });
    inflightRef.current = decodeQueueRef.current.length;
    drainDecodeQueue();
  }

  // #622 — raw PCM (Int16 LE) playback. Used by the Aura WS streaming
  // path which can't ship MP3 (Deepgram only supports linear16/mulaw/alaw
  // for streaming output). `decodeAudioData()` can't decode raw PCM
  // (it requires a container format like WAV/MP3/Ogg), so we build the
  // AudioBuffer directly.
  //
  // Resampling strategy: convert Int16 samples at `sampleRate` to Float32
  // at `ctx.sampleRate` IN JS, with stateful linear interpolation that
  // carries `pcmResamplePosFracRef` (sub-sample source position) and
  // `pcmResampleTailRef` (previous chunk's last sample) across chunk
  // boundaries. The resulting AudioBuffer is created at ctx.sampleRate
  // exactly, so the audio framework does NO further resampling at play
  // time. Adjacent buffers concatenate sample-accurately and the
  // per-chunk-boundary click goes away.
  //
  // See the long comment on the resampler state refs at the top of the
  // component for the full root-cause writeup. Pre-#622-fix the buffer
  // was created at `sampleRate` (the source rate) and Web Audio
  // resampled per-buffer at play time, padding each boundary with
  // zeros — that padding is the click.
  function schedulePcmChunk(arrayBuffer, sampleRate) {
    try {
      const ctx = getCtx();
      // Int16Array view over the raw bytes. PCM frames must be an
      // even number of bytes; if a stray byte sneaks in, drop it.
      const evenLen = arrayBuffer.byteLength - (arrayBuffer.byteLength % 2);
      const int16 = new Int16Array(arrayBuffer, 0, evenLen / 2);
      if (int16.length === 0) return;

      const dstRate = ctx.sampleRate;
      // #622 diagnostic (first PCM chunk per stream only) — surface the
      // resampling decision so we can confirm whether the fast-path
      // (no resampling, source rate matches ctx) or the stateful
      // resampler (boundary-aware linear interp) is being taken on
      // this device. Clicks reportedly persisted after #622's stateful
      // resampler shipped; if the tablet runs ctx at 16 kHz (same as
      // Aura's Dutch native rate) the fast-path triggers and there's
      // no resampling happening at all — meaning the click is from
      // AudioBufferSourceNode boundary scheduling, not from
      // resampling. That would point us at an AudioWorklet-based
      // continuous-output rewrite as the proper fix.
      if (pcmResamplePosFracRef.current === 0 && pcmResampleTailRef.current === 0) {
        console.info(
          `[#622 PCM] schedulePcmChunk: srcRate=${sampleRate} ctxRate=${dstRate} ` +
          `path=${sampleRate === dstRate ? 'fast (no resample)' : 'stateful resample'} ` +
          `chunkSamples=${int16.length}`
        );
      }

      // Fast path: source rate already matches the context. No
      // resampling needed — just normalise to Float32 and schedule.
      if (sampleRate === dstRate) {
        const float32 = new Float32Array(int16.length);
        for (let i = 0; i < int16.length; i++) {
          // 32768 (not 32767) for symmetric range — keeps the
          // most-negative Int16 sample (-32768) at exactly -1.0
          // instead of clipping past it.
          float32[i] = int16[i] / 32768;
        }
        const buffer = ctx.createBuffer(1, float32.length, dstRate);
        buffer.copyToChannel(float32, 0);
        // Keep the resampler state in a sane place for the next
        // chunk in case the rate changes mid-stream. tail = last
        // sample of this chunk; posFrac = 0 (no sub-sample carry
        // when we're not resampling).
        pcmResamplePosFracRef.current = 0;
        pcmResampleTailRef.current    = int16[int16.length - 1];
        scheduleBuffer(buffer);
        return;
      }

      // Stateful linear interpolation from `sampleRate` → `dstRate`.
      //
      // For output sample `i`, source position is
      //   srcIdx = posFrac + i * ratio
      // where ratio = sampleRate / dstRate. Floor + frac give the
      // two neighbouring source samples to interpolate between.
      //
      // The first output sample of THIS chunk may need to interpolate
      // between the PREVIOUS chunk's tail (srcIdx in [-1, 0)) and
      // this chunk's first sample. `pcmResampleTailRef` provides that
      // tail. Without it the boundary would be (silent → first sample)
      // which is exactly the click we're fixing.
      const ratio = sampleRate / dstRate;
      const posFracIn = pcmResamplePosFracRef.current;
      const tail = pcmResampleTailRef.current;

      // Output length: produce samples while srcIdx < int16.length.
      // outLen = floor((int16.length - posFracIn) / ratio).
      const outLen = Math.max(0, Math.floor((int16.length - posFracIn) / ratio));
      if (outLen === 0) {
        // Source chunk too small to produce any output at this ratio
        // — defer the state advance to the next chunk. (Aura chunks
        // are typically thousands of samples; this branch is purely
        // defensive against degenerate tiny chunks.)
        pcmResamplePosFracRef.current = posFracIn - int16.length;
        pcmResampleTailRef.current    = int16[int16.length - 1];
        return;
      }

      const float32 = new Float32Array(outLen);
      const lastIdx = int16.length - 1;
      for (let i = 0; i < outLen; i++) {
        const srcIdx = posFracIn + i * ratio;
        const srcIdxFloor = Math.floor(srcIdx);
        const frac = srcIdx - srcIdxFloor;
        let s0, s1;
        if (srcIdxFloor < 0) {
          // Between previous chunk's tail and this chunk's [0].
          s0 = tail;
          s1 = int16[0];
        } else if (srcIdxFloor >= lastIdx) {
          // At the very end — duplicate the last sample for the
          // s1 slot. outLen was computed so this branch only fires
          // for the final output sample at most.
          s0 = int16[lastIdx];
          s1 = int16[lastIdx];
        } else {
          s0 = int16[srcIdxFloor];
          s1 = int16[srcIdxFloor + 1];
        }
        // Linear interp + normalise. 32768 (not 32767) keeps the
        // symmetric range for the most-negative Int16 value.
        float32[i] = (s0 + (s1 - s0) * frac) / 32768;
      }

      // Carry state to the next chunk. The next chunk's posFrac is
      // the source-position drift past the last sample we consumed,
      // measured in source-sample units. With outLen samples produced
      // at step `ratio` from posFracIn, the next source position is
      //   posFracIn + outLen * ratio
      // and the next chunk's first input sample is at source index
      // `int16.length`, so the new posFrac (relative to that chunk)
      // is the difference.
      pcmResamplePosFracRef.current = posFracIn + outLen * ratio - int16.length;
      pcmResampleTailRef.current    = int16[lastIdx];

      const buffer = ctx.createBuffer(1, float32.length, dstRate);
      buffer.copyToChannel(float32, 0);
      scheduleBuffer(buffer);
    } catch (err) {
      console.warn('AudioPlayer: PCM schedule failed', err);
    }
  }

  // Schedule a short silent buffer to keep the audio graph active (prevents OS codec sleep)
  function scheduleSilentPad(durationSeconds = 0.1) {
    const ctx = getCtx();
    const silentBuffer = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * durationSeconds), ctx.sampleRate);
    const source = ctx.createBufferSource();
    source.buffer = silentBuffer;
    source.connect(ctx.destination);
    const startAt = Math.max(ctx.currentTime, nextStartRef.current);
    source.start(startAt);
    nextStartRef.current = startAt + durationSeconds;
  }

  useImperativeHandle(ref, () => ({

    addChunk: (arrayBuffer) => {
      decodeAndSchedule(arrayBuffer);
    },

    // #622 — raw 16-bit signed little-endian PCM at `sampleRate` Hz.
    // Used by the Aura WS streaming path. Bypasses the decode queue
    // (no codec to invoke) and schedules directly. Caller is
    // responsible for choosing the right path: addChunk() for
    // encoded audio (MP3/WAV/OGG), addPcmChunk() for raw PCM bytes.
    addPcmChunk: (arrayBuffer, sampleRate = 24000) => {
      schedulePcmChunk(arrayBuffer, sampleRate);
    },

    // Play a pre-buffered list of ArrayBuffers, fire onDone when all finish
    playBuffer: (chunks) => {
      if (!chunks || chunks.length === 0) {
        if (typeof onDoneRef.current === 'function') onDoneRef.current();
        return;
      }
      pendingDoneRef.current = true;
      chunks.forEach(ab => decodeAndSchedule(ab));
    },

    // Server signals it has sent all audio chunks for this turn
    signalServerDone: () => {
      pendingDoneRef.current = true;
      // If already idle (all decoded & played), fire onDone immediately.
      // Guard: if still decoding the last chunk (decodingRef=true), inflight
      // is already 0 but the buffer hasn't been scheduled yet — defer to
      // drainDecodeQueue's post-loop check to avoid firing before audio plays.
      if (pendingRef.current <= 0 && inflightRef.current <= 0 && !isPlayingRef.current && !decodingRef.current) {
        pendingDoneRef.current = false;
        if (typeof onDoneRef.current === 'function') onDoneRef.current();
      }
    },

    stop: () => {
      // Close and recreate the AudioContext — cleanest way to stop everything
      if (ctxRef.current) {
        ctxRef.current.close().catch(() => {});
        ctxRef.current = null;
      }
      decodeQueueRef.current = [];
      decodingRef.current = false;
      nextStartRef.current = 0;
      pendingRef.current = 0;
      inflightRef.current = 0;
      isPlayingRef.current = false;
      pendingDoneRef.current = false;
      // #605 — log per-stream summary if there were any failures, then
      // reset counters for the next stream. Successful streams stay quiet.
      if (decodeStatsRef.current.failed > 0) {
        console.info(
          `[#605 AudioPlayer] stream ended: ${decodeStatsRef.current.ok} ok / ` +
          `${decodeStatsRef.current.failed} failed (out of ${chunkSeqRef.current} total)`
        );
      }
      chunkSeqRef.current = 0;
      decodeStatsRef.current = { ok: 0, failed: 0, firstHeadHex: null };
      // #622 — reset the PCM resampler state between streams so
      // the next turn's first chunk starts from posFrac=0 / tail=0
      // (silence) rather than carrying drift / tail samples from
      // the previous turn. Without this reset, a long call would
      // accumulate sub-sample drift over hundreds of chunks and a
      // sample-rate change between turns (Aura voice swap mid-call,
      // hypothetically) would have a stale tail to start from.
      pcmResamplePosFracRef.current = 0;
      pcmResampleTailRef.current    = 0;
    },

    isPlaying: () => isPlayingRef.current,

    warmUp: () => {
      // Call inside a user-gesture handler to unlock AudioContext early
      getCtx();
    },

    // Keep the audio graph alive (prevent OS codec sleep between clips)
    keepAlive: () => scheduleSilentPad(0.05),
  }));

  // Close AudioContext on unmount to prevent leaks
  useEffect(() => {
    return () => {
      if (ctxRef.current) {
        ctxRef.current.close().catch(() => {});
        ctxRef.current = null;
      }
      pendingRef.current = 0;
      isPlayingRef.current = false;
    };
  }, []);

  return null;
});

export default AudioPlayer;
